'use client';

import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { formatNumber } from '../../../../../../apps/frontend/src/utils/utilFunctions';

export default function ScorePotentialChart({ selectedSubject, scoreImprovement, showDecimals = false }) {
  const [currentSubjectData, setCurrentSubjectData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [yAxisDomain, setYAxisDomain] = useState([0, 100]);

  useEffect(() => {
    const filteredData = scoreImprovement?.filter(
      (item) => item.subjectMeta._id.toString() === selectedSubject.toString(),
    );
    const subjectData = filteredData?.[0];
    setCurrentSubjectData(subjectData);

    if (subjectData) {
      const { actual, improvement } = subjectData;
      const rawActualScore = Number(actual.actualScore) ?? 0;
      const clampedActualScore = Math.max(rawActualScore, 0); // keep bars at or above 0

      const computedChartData = [
        {
          name: 'Actual score',
          actualScore: clampedActualScore,
          improvedScore: 0,
        },
        ...improvement.map((item) => ({
          name: `${item.reductionPercentage}% less error`,
          actualScore: clampedActualScore,
          improvedScore: Math.max(item.improvedScoreByErrorReduction - clampedActualScore, 0),
        })),
      ];

      setChartData(computedChartData);

      const max = Math.max(
        subjectData.actual.maxScore,
        ...computedChartData.map((d) => d.actualScore + d.improvedScore),
      );
      setYAxisDomain([0, Math.ceil(max * 1.1)]); // y-axis always starts at 0
    } else {
      setChartData([]);
      setYAxisDomain([0, 110]);
    }
  }, [selectedSubject, scoreImprovement]);

  const CustomBarLabel = ({ x, y, width, index }) => {
    if (!currentSubjectData) return null;

    const max = currentSubjectData?.actual?.maxScore || 0;
    const isActualBar = index === 0;

    const rawValue = isActualBar
      ? currentSubjectData.actual?.actualScore
      : currentSubjectData.improvement?.[index - 1]?.improvedScoreByErrorReduction;

    if (rawValue == null) return null;

    const formatted = showDecimals ? formatNumber(rawValue) : Math.round(rawValue);

    return (
      <g transform={`translate(${x + width / 2}, ${y - 8})`} textAnchor="middle">
        <text
          style={{
            fontWeight: 'bold',
            fontSize: '12px',
            fill: 'var(--q3-neutral-default)',
          }}
        >
          {formatted}
          <tspan
            style={{
              fontWeight: 'normal',
              fontSize: '10px',
              fill: 'var(--q3-neutral-light)',
            }}
          >
            /{max}
          </tspan>
        </text>
      </g>
    );
  };

  if (!currentSubjectData) return null;

  const gradientId = `improvedScoreGradient-${currentSubjectData?.subjectMeta?._id}`;
  const baseColor = 'var(--q3-accent-normal)';

  // Calculate minimum width based on number of bars
  // Each bar needs space + margins, minimum 120px per bar for readability
  const minWidth = Math.max(chartData.length * 120, 400);

  return (
    <div className="h-[300px] border border-t-0 overflow-x-auto rounded-b-xl border-[var(--q3-stroke-light)] pt-4 pointer-events-none">
      <div style={{ minWidth: `${minWidth}px`, height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barSize={100} margin={{ left: 10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
              stroke="var(--q3-stroke-light)"
              axisLine={{ stroke: 'var(--q3-stroke-light)' }}
              interval={0} // Show all labels
            />
            <YAxis
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
              width={50}
              stroke="var(--q3-stroke-light)"
              axisLine={{ stroke: 'var(--q3-stroke-light)' }}
            />
            <defs>
              <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor={baseColor} stopOpacity={0.1} />
                <stop offset="100%" stopColor={baseColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <Bar dataKey="actualScore" stackId="a" fill={baseColor} />
            <Bar dataKey="improvedScore" stackId="a" fill={`url(#${gradientId})`} label={<CustomBarLabel />} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
